// ---------------------------------------------------------------------------
// adminLabels.ts — human-readable labels for admin/audit surfaces.
//
// event_type strings collected from:
//   - supabase/migrations/0014_meeting_events.sql (header list)
//   - backend/app/main.py  (log_event calls)
//   - backend/app/jobs/transcribe.py (log_event calls)
//   - frontend/src/pages/MeetingReview.tsx and other SPA callers
// ---------------------------------------------------------------------------

export const EVENT_LABELS: Record<string, string> = {
  // ---- system: upload / soniox ----
  upload_completed: "Audio uploaded",
  context_review_pending: "Context review pending",
  soniox_started: "Transcription started",
  soniox_completed: "Transcription completed",
  soniox_failed: "Transcription failed",
  post_soniox_failed: "Post-transcription error",

  // ---- system: annotation detectors ----
  attribution_completed: "Speaker attribution done",
  attribution_critique_completed: "Attribution critique done",
  sentinel_completed: "Sentinel review done",
  glossary_mined: "Glossary terms mined",
  deeper_attribution_skipped: "Deep attribution skipped",
  annotation_failed: "Annotation failed",
  annotations_completed: "Annotation finished",
  drift_detected: "Drift detected",

  // ---- system: pipeline misc ----
  context_curated: "Context curated",
  project_change: "Project changed",

  // ---- system: cost-bearing inference (future events, labels provided now) ----
  inference_anthropic: "AI annotation",
  inference_soniox: "Speech-to-text",

  // ---- system: lock / unlock ----
  lock: "Locked",
  unlock: "Unlocked",

  // ---- system: exports / downloads ----
  export_markdown: "Exported (Markdown)",
  export_json: "Exported (JSON)",
  audio_download: "Downloaded audio",

  // ---- user: title / speaker ----
  title_change: "Renamed",
  speaker_label_override: "Renamed a speaker",

  // ---- user: text corrections ----
  text_correction_accept: "Edited text",
  text_correction_dismiss: "Undid correction",

  // ---- user: span attribution ----
  span_attribution_add: "Assigned a speaker",
  span_attribution_remove: "Removed speaker assignment",
  span_attribution_rename: "Renamed span speaker",

  // ---- user: paragraph operations ----
  paragraph_split: "Split paragraph",
  paragraph_rejoin: "Rejoined paragraph",
  paragraph_revert: "Reset paragraph",
  edit_session: "Paragraph edit session",

  // ---- user: bookmarks ----
  bookmark_set: "Bookmarked paragraph",
  bookmark_clear: "Removed bookmark",

  // ---- user: lifecycle ----
  resubmit_initiated: "Re-ran transcription",
  undo_action: "Undid action",
  transcript_deleted: "Transcript deleted",
  transcript_restored: "Transcript restored",
};

/** Returns a human label for event_type; unknown codes pass through as-is. */
export function labelForEvent(t: string): string {
  return EVENT_LABELS[t] ?? t;
}

/**
 * Formats a USD amount.
 * - 0            → "$0.00"
 * - ≥ 0.01       → "$N.NN" (2 decimal places)
 * - 0 < x < 0.01 → more decimal places to show meaningful precision (up to 6)
 */
export function formatUsd(n: number): string {
  if (n === 0) return "$0.00";
  if (n < 0.01 && n > 0) {
    // Show up to 6 significant decimal digits, stripping trailing zeros.
    const fixed = n.toFixed(6);
    // Strip trailing zeros but keep at least 4 decimal places for clarity.
    const stripped = fixed.replace(/(\.\d{4,}?)0+$/, "$1");
    return `$${stripped}`;
  }
  return `$${n.toFixed(2)}`;
}

/**
 * Formats a byte count as human-readable storage string.
 * 0 → "0 B", otherwise in B / KB / MB / GB (1024-based).
 */
export function formatBytes(n: number): string {
  if (n === 0) return "0 B";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/** First 8 chars of a UUID — a stable short label for ids in admin tables. */
export function shortId(id: string): string {
  return id.slice(0, 8);
}
